<template>
  <div class="kb-shell kb-screen-h flex relative overflow-hidden bg-kb-bg">
    <DashboardSidebar
      ref="sidebarRef"
      :is-admin="isAdmin"
      :is-band-admin="isBandAdmin"
      :is-redshirt-admin="isRedshirtAdmin"
      :is-redshirt-lieutenant="isRedshirtLieutenant"
      :is-checkin-volunteer="isCheckinVolunteer"
      :is-captain="isCaptain"
      :has-subkrewe="hasSubkrewe"
    />

    <div class="kb-screen-h min-w-0 flex flex-col relative flex-auto">
      <DashboardTopBar
        :display-name="displayName"
        :is-admin="isAdmin"
        class="flex-shrink-0 z-10"
      />

      <main class="kb-autoscrollbar relative flex-1 min-h-0 overflow-auto">
        <div class="p-2 h-full min-h-0 flex flex-col gap-2">
          <div class="flex-1">
            <slot />
          </div>
        </div>
      </main>

      <DashboardStatusLine
        class="hidden lg:flex"
        :can-search="canSearch"
      />
      <DashboardMobileTabBar :has-subkrewe="hasSubkrewe" />

      <DashboardDrawer
        :is-open="isDrawerOpen"
        :title="drawerTitle"
        :content="drawerContent"
        :content-props="{}"
        :can-go-back="canGoBack"
        @close="closeDrawer"
        @back="goBackInDrawer"
        @reset-history="resetDrawerHistory"
      />
    </div>
    <DashboardRedshirtRollingPrompt :member="currentMember" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, provide, ref } from 'vue'
import { useRouter } from 'vue-router'

const sidebarRef = ref(null)
const router = useRouter()

// Get current user and reactive member data from store
const currentUser = useCurrentUser()
const membersStore = useMembersStore()

// Get current member from store reactively
const memberPath = computed(() => {
  const uid = currentUser.value?.uid
  return uid ? `members/${uid}` : null
})

// Get member data from store using the path - this is reactive!
const currentMember = computed(() => {
  if (!memberPath.value) return null
  return membersStore.members[memberPath.value] || null
})

// Reactive computed properties based on member data
const displayName = computed(() => currentMember.value?.name || '')
const memberRoles = computed(() => currentMember.value?.roles || {})
const isAdmin = computed(() => memberRoles.value?.admin === true)
const isBandAdmin = computed(() => memberRoles.value?.band_admin === true)
const isRedshirtAdmin = computed(() => memberRoles.value?.redshirt_admin === true)
const isRedshirtLieutenant = computed(() => currentMember.value?.isRedshirtLieutenant === true)
const isCheckinVolunteer = computed(() => memberRoles.value?.checkinVolunteer === true || memberRoles.value?.admin === true)
const isCaptain = computed(() => currentMember.value?.member_type === 'captain')
// Mirrors where layouts/default.vue mounts the command palette
const canSearch = computed(() => isAdmin.value || isBandAdmin.value || isRedshirtAdmin.value)
const hasSubkrewe = computed(() => !!currentMember.value?.subkrewe?.id)

// Method to close the sidebar (will be called when a link is clicked)
const closeSidebar = () => {
  // Check if we're on mobile (non-desktop)
  if (window.innerWidth < 1024) {
    document.getElementById('app-sidebar-9')?.classList.add('hidden')
  }
}

onMounted(async () => {
  // Ensure current member is loaded into the store
  if (currentUser.value?.uid) {
    await membersStore.fetchMember(currentUser.value.uid)
  }

  // Listen for route changes to close the sidebar
  if (router) {
    router.afterEach(() => {
      closeSidebar()
    })
  }
})

// Use the drawer composable with the new functionality
const {
  isDrawerOpen,
  drawerTitle,
  drawerContent,
  canGoBack,
  closeDrawer,
  goBackInDrawer,
  resetDrawerHistory,
} = useDrawer()

// Provide memberRoles to child components
provide('memberRoles', memberRoles)
</script>

<style>
/* Global styles to ensure proper scrolling behavior for DataTable */
.dashboard-content {
  width: fit-content;
  min-width: 100%;
}

/* Ensure other components don't get clipped */
.card {
  overflow: visible;
  width: fit-content;
  min-width: 100%;
}
</style>

<style scoped>
/* Adjust flex properties to prevent excessive expansion */
.flex-auto {
  max-width: 100%;
  overflow-x: hidden;
}

.flex-1 {
  max-width: 100%;
  overflow-x: hidden;
}
</style>
